/**
 * Travel Store - Global state management
 * Sử dụng useModel của @umijs/max
 */

import { getPlaces } from '@/services/maps/travel/api';
import type { AdminPlace, AdminPlaceListParams, ApiListResponse } from '@/services/maps/travel/types';
import { message } from 'antd';
import { useCallback, useState } from 'react';

/** Type cho return value của useTravelStore */
export interface TravelStoreModel {
  places: AdminPlace[];
  hasFetched: boolean;
  hasUploadError: boolean;
  fetchPlaces: (params?: AdminPlaceListParams) => Promise<ApiListResponse<AdminPlace> | null>;
  addPlace: (newPlace: AdminPlace) => void;
  updatePlace: (updatedPlace: AdminPlace) => void;
  removePlace: (placeId: string) => void;
  setHasUploadError: (hasError: boolean) => void;
}

export default function useTravelStore(): TravelStoreModel {
  const [places, setPlaces] = useState<AdminPlace[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);

  const fetchPlaces = useCallback(async (params?: AdminPlaceListParams) => {
    try {
      const response = await getPlaces(params);
      if (response.success) {
        setPlaces(response.data);
        setHasFetched(true);
      }
      return response;
    } catch (error) {
      message.error('Lỗi khi tải danh sách địa điểm');
      return null;
    }
  }, []);

  const addPlace = useCallback((newPlace: AdminPlace) => {
    if (hasFetched) {
      setPlaces((prev) => [newPlace, ...prev]);
    }
  }, [hasFetched]);

  const updatePlace = useCallback((updatedPlace: AdminPlace) => {
    if (hasFetched) {
      setPlaces((prev) =>
        prev.map((place) => (place.id === updatedPlace.id ? updatedPlace : place))
      );
    }
  }, [hasFetched]);

  const removePlace = useCallback((placeId: string) => {
    if (hasFetched) {
      setPlaces((prev) => prev.filter((place) => place.id !== placeId));
    }
  }, [hasFetched]);

  return {
    places,
    hasFetched,
    hasUploadError,
    fetchPlaces,
    addPlace,
    updatePlace,
    removePlace,
    setHasUploadError,
  };
}
